<template>
    <div class="api-key-widget">
        <div class="widget-contents-container">
            <div class="widget-header">
                <h3>Generate New API Key</h3>
                <div class="svg-container widget-close-button" @click="closeWidget">
                    <widget-close-svg />
                </div>
            </div>

            <div class="content-wrapper">
                <loading-screen-svg v-if="isLoading" />

                <div v-else-if="!showFullAPIKeyToClient" class="form-wrapper">
                    <div class="checkmark-container">
                        <div class="input-valid" v-if="$v.apiKey.name.required">
                            <i21-checkmark-circle-svg />
                        </div>
                        <div v-else class="input-not-valid">
                            <r3-checkmark-svg />
                        </div>
                    </div>   
                    <div class="copy-input">
                        <h5>API Key Name</h5>
                        <span class="subtext">This is the internal name in your API keys list.</span>
                        <app-input 
                            v-model="apiKey.name"
                            placeholder="API key name"
                            :error="`Name is required`"
                            :isValid="!submitted || $v.apiKey.name.required"
                        
                        
                        />
                    </div>
                </div>
                <div v-else class="form-wrapper">
                    <div class="copy-input">
                        <h5>Here’s your secret API key</h5>
                        <span class="subtext">We only show this to you once (right now). If you forget it, you’ll need to generate a new one.</span>
                        <div class="input-container">
                            <app-input 
                            v-model="currentAPIKey"
                            label=""
                            :disabled="true"
                        
                        />

                        <app-button v-if="!onceClickCopy" class="copy-btn" @click="clickCopyBtn">
                            Copy
                        </app-button>

                        <app-button v-else class="copy-btn blue-ghost-btn" @click="clickCopyBtn">
                            Copied!
                        </app-button>
                        </div>
                    </div>
                </div>
            </div>
            <div class="widget-footer">
                <div class="cancel-container">
                    <app-button class="alternate" @click="closeWidget">
                        {{ showFullAPIKeyToClient ? 'Close' : 'Cancel' }}
                    </app-button>
                </div>
                <div v-if="!showFullAPIKeyToClient" class="action-container">
                    <app-button v-if="$v.apiKey.name.required" @click="save()">Next Step <m6-next-svg />  </app-button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>

const AppButton = () => import('@/components/common/app-button.vue');
const WidgetCloseSvg = () => import('@/components/svg/quick-add/widget-close-svg.vue');
const AppInput = () => import('@/components/common/app-input.vue');
const LoadingScreenSvg = () => import('@/components/svg/loading-screen-svg');
const I21CheckmarkCircleSvg = () => import('@/components/svg/grid/i21-checkmark-circle-svg')
const R3CheckmarkSvg = () => import('@/components/svg/grid/r3-checkmark-svg');
const M6NextSvg = () => import('@/components/svg/grid/m6-next-svg')

import { APIKeysService } from '@/scripts/services/open-api/api-keys-service.js';
import EventBus from '@/scripts/event-bus.js';
import { required} from 'vuelidate/lib/validators';


export default {
    name: 'api-key-widget',

    components: {
        'app-button': AppButton,
        'widget-close-svg': WidgetCloseSvg,
        'app-input': AppInput,
        'loading-screen-svg': LoadingScreenSvg,
        'i21-checkmark-circle-svg': I21CheckmarkCircleSvg,
        'm6-next-svg': M6NextSvg,
        'r3-checkmark-svg': R3CheckmarkSvg
    },

    props: {
        widget: {
            type: Object,
            required: true
        }
    },

    data() {
        return {
            submitted: false,
            isLoading: false,
            showFullAPIKeyToClient: false,
            currentAPIKey: null,
            onceClickCopy: false,
            apiKey: {
                name: null
            }
        }
    },

    validations: {
        apiKey: {
            name: {
                required
            },
        },
    },

    methods: {
        closeWidget() {
            EventBus.$emit('close-widget', this.widget);
        },

        save() {
            if (this.$v.invalid) {
                return;
            }

            this.post();
        },

        post() {
            this.isLoading = true;

            const body = {
                name: this.apiKey.name
            }

            APIKeysService.createAPIKey(this.widget.accountId, body)
                .then(({ data }) => {
                    this.isLoading = false;
                    this.showFullAPIKeyToClient = true;
                    this.currentAPIKey = data.data.ApiKey;
                    this.$helpers.showSuccessNotification('Your API Key has been created successfully!')
                    EventBus.$emit('openapi-board:updated');
                }) 
                .catch((error) => {
                    this.isLoading = false;
                    this.$helpers.showFailNotification('An error occurred while creating your API key.', { error });
                })
        },

        clickCopyBtn() {
            this.$helpers.copyTextToClipboard(this.currentAPIKey);
            this.onceClickCopy = true;
        }
    }
}
</script>

<style>

.api-key-widget .copy-input {
    display: flex;
    flex-direction: column;
    width: 100%;
}

.api-key-widget .copy-input h5 {
    font-size: 20px;
}

.api-key-widget .input-container {
    display: flex;
}

.api-key-widget .input-container .app-input {
    width: 100%;
}

.api-key-widget .subtext {
    margin-bottom: 10px;
    font-size: 14px;
    font-family: "Roboto", sans-serif;
}

.api-key-widget .input-container .copy-btn {
    margin-left: 16px;
    padding: 8px 34px 11px 34px;
    width: 122px;
}

.api-key-widget .cancel-container .alternate.app-button.btn.normal {
    background-color: transparent;
    border: 1px solid #CAC6CB;
    border-radius: 20px;
    opacity: 1;
}

.api-key-widget .widget-contents-container .copy-btn.blue-ghost-btn {
	color: #18aeff;
	border: solid 1px #18aeff;
	background-color: transparent;
	-webkit-transition: all 500ms ease-out;
	-moz-transition: all 500ms ease-out;
	-o-transition: all 500ms ease-out;
	transition: all 500ms ease-out;
}

.api-key-widget .action-container .m6-next-svg {
    height: 12px;
    margin-left: 16px;
    width: 16px;
}

.api-key-widget .action-container .m6-next-svg .a {
        fill: #fff;
    }

.api-key-widget div.content-wrapper {
    padding: 32px;
}

.api-key-widget .form-wrapper {
    display: flex;
    gap: 16px;
}

.api-key-widget .checkmark-container {
    width: 24px;
    height: 24px;
}


.api-key-widget .checkmark-container .r3-checkmark-svg {
    width: 14px;
    height: 12px;
    position: relative;
    top: 2px;
    left: 3px;
}

.api-key-widget .checkmark-container .i21-checkmark-circle-svg {
    width: 100%;
    height: 100%;
}

.api-key-widget .checkmark-container .r3-checkmark-svg .a {
    fill: #CAC6CB;
}

.api-key-widget .widget-contents-container .widget-header {
    padding-left: 24px;
}

.api-key-widget .checkmark-container .input-not-valid {
    border: 1px solid #CAC6CB;
    border-radius: 50%;
    background: #FAFAFA 0% 0% no-repeat padding-box;
    width: 23px;
    height: 23px;
}

.api-key-widget .form-wrapper .copy-input .app-input input {
    height: 40px;
}

</style>